import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HourglassTopTwoToneIcon from '@mui/icons-material/HourglassTopTwoTone';

function LineupStatus({groupId, group, setGroup, waitingTime, batchesAhead, updateLoading, fetchGroup}) {

    const [joinTime, setJoinTime] = useState(null);
    const [updatedTime, setUpdatedTime] = useState(null);
    const [openCancel, setOpenCancel] = useState(false); //cancel dialogue box
    const [showNotificationCard, setShowNotificationCard] = useState(false);


    useEffect(() => {

        //update timestampjoin if available
        if (group?.timestampJoin) {
            const groupJoinTime = new Date(group.timestampJoin);
            const formattedJoinTime = new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                hour12: 'true'
            }).format(groupJoinTime);
            setJoinTime(formattedJoinTime);
        }
        //show notification card if status is 'NOTIFIED' or 'NOSHOW'
        if (group?.status) {
            const status = group.status;
            if (status === "NOSHOW" || status === "NOTIFIED") {
                setShowNotificationCard(true);
            } else {
                setShowNotificationCard(false);
            }
        }

        //update updated time
        const currentTime = new Date();
        const formattedUpdateTime = new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: 'true'
        }).format(currentTime);
        setUpdatedTime(formattedUpdateTime);

    }, [group]);


    //open cancel dialog
    const handleOpenCancel = () => {
        setOpenCancel(true);
    };

    //close cancel dialog
    const handleCloseCancel = () => {
        setOpenCancel(false);
    };

    const handleCancel = async (event) => {
        event.preventDefault();

        try {
            const response = await axios.put(`/api/groups/${groupId}/cancel`);
            setGroup(response.data);
        } catch (error) {
            console.error("Error leaving line-up: ", error);
        } finally {
            fetchGroup();
        }
    };

    const standByCard = () => {
        return (
            <Card elevation={1} rounded="true" sx={{backgroundColor: '#24305e', margin: 1}}>
                <CardContent>
                    <Typography sx={{
                        display: 'flex',
                        fontSize: '1.1rem',
                        lineHeight: 1,
                        fontWeight: 600,
                        m: 0, p: 0,
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <HourglassTopTwoToneIcon sx={{mr: 1}}/>
                        {"Not your turn yet"}
                    </Typography>
                    <Typography align='center' sx={{fontSize: "0.9rem", fontWeight: 400}}>
                        {"Visit other telescopes and stargaze! Check back frequently to know when to come to the observatory entrance."}
                    </Typography>
                </CardContent>
            </Card>
        );
    };

    const notificationCard = () => {
        return (
            <Card elevation={1} rounded="true"   sx={{
                backgroundColor: group.status === "NOSHOW" ? '#A6192E' : '#245E28',
                margin: 1
            }}>
                <CardContent>
                    <Typography sx={{
                        display: 'flex',
                        fontSize: '1.1rem',
                        lineHeight: 1,
                        fontWeight: 600,
                        m: 0, p: 0,
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <NotificationsActiveIcon sx={{mr: 1}}/>
                        {(group.status === "NOSHOW") ? "No-Show Notice"
                            : "Proceed to the entrance."
                        }
                    </Typography>
                    <Typography align='center' sx={{fontSize: "0.9rem", fontWeight: 400}}>
                        {(group.status === "NOSHOW") ? "We missed you when it was your turn to enter the Observatory. Please stay nearby the entrance when it is close to your turn again. Staff will call out your group name."
                            : "You are up soon! Staff will call your group name at the Observatory entrance when it is your turn."
                        }
                    </Typography>
                </CardContent>
            </Card>
        );
    };

    return (
        <Card elevation={9} rounded="true">
            <CardContent>
                {group ? (<>
                        <Box
                            display="flex"
                            justifyContent="flex-start"
                            sx={{mt: 0}}
                        >
                            <Button
                                variant="outlined"
                                endIcon={<CancelIcon/>}
                                onClick={handleOpenCancel}
                                size="small"
                                color="primary"
                                sx={{fontSize: '0.8rem', fontWeight: '600'}}>
                                Exit Line
                            </Button>
                            <Dialog
                                open={openCancel}
                                onClose={handleCloseCancel}
                                aria-labelledby="dialog-title"
                                aria-describedby="dialog-description"
                            >
                                <DialogTitle id="dialog-title">
                                    Leave your spot in the line?
                                </DialogTitle>
                                <DialogContent>
                                    <DialogContentText id="dialog-description">
                                        You are currently in position {group.position} for the Trottier Observatory.
                                        Are you sure you would like to leave?
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions sx={{display: 'flex', justifyContent: 'space-evenly'}}>
                                    <Button onClick={handleCloseCancel} autoFocus>Stay</Button>
                                    <Button onClick={handleCancel}>
                                        Leave
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                        <Typography align="center"
                                    sx={{fontSize: '1.2rem', fontWeight: 500, padding: '10px 5px 10px 5px'}}>
                            Your Details
                        </Typography>
                        <Grid container spacing={0.3}>
                            <Grid item xs={4}>
                                <Typography align="left"
                                            sx={{fontSize: '0.9rem', fontWeight: 500}}>
                                    Name:
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography align="left"
                                            sx={{fontSize: '1rem', fontWeight: 700}}>
                                    {group.name}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography align="left"
                                            sx={{fontSize: '0.9rem', fontWeight: 500}}>
                                    Group Size:
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography align="left"
                                            sx={{fontSize: '1rem', fontWeight: 700}}>
                                    {group.size}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography align="left"
                                            sx={{fontSize: '0.9rem', fontWeight: 500}}>
                                    Entered Line:
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography align="left"
                                            sx={{fontSize: '1rem', fontWeight: 700}}>
                                    {joinTime}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography align="left"
                                            sx={{fontSize: '0.9rem', fontWeight: 500}}>
                                    Identifier:
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography align="left"
                                            sx={{fontSize: '0.9rem', fontWeight: 700, color: "#ce93d8"}}>
                                    {group.code}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Card elevation={3} rounded="true" sx={{mt: 1}}>
                                    <CardContent sx={{margin: 0, padding: 0}}>
                                        {showNotificationCard ? notificationCard() : standByCard()}
                                        <Stack spacing={0}>
                                            <Typography align="center"
                                                        sx={{
                                                            fontSize: '0.8rem',
                                                            lineHeight: 1,
                                                            fontWeight: 500,
                                                            mt: 2,
                                                            color: "#bcbcbc"
                                                        }}>
                                                Last Updated: {updatedTime}
                                                <span style={{display: 'inline-block', width: '30px'}}>
                                                    {updateLoading && <CircularProgress
                                                        sx={{ml: '5px'}}
                                                        size="0.7rem"
                                                    />}
                                                </span>
                                            </Typography>
                                            <Typography align="center"
                                                        sx={{
                                                            fontSize: '1.2rem',
                                                            lineHeight: 1,
                                                            fontWeight: 600,
                                                            mb: 0,
                                                            pb: 0,
                                                            pt: 2
                                                        }}>
                                                Group Name
                                            </Typography>
                                            <Typography align="center"
                                                        sx={{
                                                            fontSize: '3.5rem',
                                                            lineHeight: 1,
                                                            fontWeight: 700,
                                                            mt: 0,
                                                            pt: 0
                                                        }}>
                                                {group.batchName}
                                            </Typography>
                                            <Typography align="center"
                                                        sx={{fontSize: '1rem', lineHeight: 1, fontWeight: 500, mt: 1}}>
                                                Estimated waiting left: {waitingTime} minute(s)
                                            </Typography>
                                            <Typography align="center"
                                                        sx={{fontSize: '1rem', lineHeight: 1, fontWeight: 500, mt: 1}}>
                                                {batchesAhead} group(s) ahead of you.
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>

                            </Grid>
                            <Grid item xs={12}>
                                <Typography align="center"
                                            sx={{
                                                fontSize: '0.8rem',
                                                lineHeight: 1.2,
                                                fontWeight: 600,
                                                mt: 2,
                                                color: "#ffb74d"
                                            }}>
                                    * Please check site consistently for notification to come to the entrance *
                                </Typography>
                                <Typography align="center"
                                            sx={{
                                                fontSize: '0.8rem',
                                                lineHeight: 1.2,
                                                fontWeight: 400,
                                                color: "#ffb74d"
                                            }}>
                                </Typography>
                            </Grid>

                        </Grid>

                    </>
                ) : (
                    <Box
                        display="flex"
                        justifyContent="center"
                        sx={{mt: 10, mb: 10}}
                    >
                        <CircularProgress/>
                    </Box>)}
            </CardContent>
        </Card>

    )
}

export default LineupStatus;